import React, { useState, useRef, useEffect } from 'react';
import API_BASE_URL from '../config';
import {
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Box,
  Paper,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import * as d3 from 'd3';

// Constants
const TOTAL_AREAS = 10;
const ALLOWED_AREAS = [8, 10];
const COLORS = {
  primary: '#1990bb',
  primaryLight: 'rgba(25, 144, 187, 0.1)',
  primaryMedium: 'rgba(25, 144, 187, 0.3)',
  primaryDark: 'rgba(25, 144, 187, 0.8)',
  hover: 'rgba(25, 144, 187, 0.4)', // Made hover color more visible
  text: '#333333',
  shapeFill: 'rgba(25, 144, 187, 0.6)', // Made shape fill much darker
};

// D3 wheel component
export const LifeWheel = ({ areas, scores, width = 800, height = 600 }) => {
  const [dimensions, setDimensions] = useState({ width, height });

  useEffect(() => {
    const updateDimensions = () => {
      const isMobile = window.innerWidth < 600;
      const newWidth = isMobile ? window.innerWidth - 32 : width;
      const newHeight = isMobile ? window.innerWidth - 32 : height;
      setDimensions({ width: newWidth, height: newHeight });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, [width, height]);
  const svgRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current) return;

    // Store current ref value for cleanup
    const currentSvg = svgRef.current;
    const { width, height } = dimensions;

    // Filter out empty areas and their corresponding scores
    const validAreas = areas.filter((area) => area.trim() !== '');
    const validScores = scores.filter((_, i) => areas[i].trim() !== '');

    // Validate inputs
    if (!validAreas.length || !validScores.length) return;
    if (validAreas.length !== validScores.length) return;

    // D3 visualization code
    const svg = d3.select(currentSvg);
    svg.selectAll('*').remove();

    // Initialize SVG with dimensions
    svg.attr('width', width)
       .attr('height', height);
      
    const margin = {
      top: width < 600 ? 50 : 100,
      right: width < 600 ? 50 : 100,
      bottom: width < 600 ? 50 : 100,
      left: width < 600 ? 50 : 100
    };
    const radius = Math.min(width - margin.left - margin.right, height - margin.top - margin.bottom) / 2.2;

    // Create SVG group for centering
    const svgGroup = svg
      .append('g')
      .attr('transform', `translate(${width/2},${height/2})`);

    // Create scales
    const angleScale = d3.scaleLinear()
      .domain([0, validAreas.length])
      .range([0, 2 * Math.PI]);

    const radiusScale = d3.scaleLinear()
      .domain([0, 10])
      .range([0, radius]);

    // Draw background circles
    const circles = d3.range(1, 11);
    svgGroup.selectAll('.grid-circle')
      .data(circles)
      .enter()
      .append('circle')
      .attr('class', 'grid-circle')
      .attr('r', d => radiusScale(d))
      .attr('fill', 'none')
      .attr('stroke', COLORS.primaryLight)
      .attr('stroke-width', d => d % 2 === 0 ? 1 : 0.5)
      .attr('opacity', 0.5);

    // Create segments with more prominent appearance
    const segments = svgGroup.selectAll('.segment')
      .data(validAreas)
      .enter()
      .append('path')
      .attr('class', (d, i) => `segment-${i}`)
      .attr('fill', COLORS.primaryLight)
      .attr('stroke', COLORS.primaryDark)
      .attr('stroke-width', 1.5)
      .attr('opacity', 0.3)
      .attr('d', (d, i) => {
        const startAngle = angleScale(i) - Math.PI/2;
        const endAngle = angleScale(i + 1) - Math.PI/2;
        
        const arc = d3.arc()
          .innerRadius(0)
          .outerRadius(radius)
          .startAngle(startAngle)
          .endAngle(endAngle)
          .padAngle(0.01); // Slightly reduce padding between segments
        
        return arc();
      });

    // Draw area labels with text wrapping
    const labelGroups = svgGroup.selectAll('.area-label-group')
      .data(validAreas)
      .enter()
      .append('g')
      .attr('class', 'area-label-group')
      .attr('transform', (d, i) => {
        const labelOffset = width < 600 ? 40 : 80;
        const x = (radius + labelOffset) * Math.cos(angleScale(i) - Math.PI/2);
        const y = (radius + labelOffset) * Math.sin(angleScale(i) - Math.PI/2);
        return `translate(${x},${y})`;
      });

    labelGroups.each(function(d) {
      const words = d.split(' ');
      let lines = [];
      let currentLine = words[0];

      // Split text into lines based on width
      for (let i = 1; i < words.length; i++) {
        const testLine = currentLine + ' ' + words[i];
        if (testLine.length * 7 > 70) { // Reduced width for better wrapping
          lines.push(currentLine);
          currentLine = words[i];
        } else {
          currentLine = testLine;
        }
      }
      lines.push(currentLine);

      // Create text elements for each line
      const textGroup = d3.select(this);
      lines.forEach((line, i) => {
        textGroup.append('text')
          .attr('x', 0)
          .attr('y', (i - (lines.length - 1) / 2) * 24)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('fill', COLORS.text)
          .attr('font-size', width < 600 ? '12px' : '16px')
          .attr('font-weight', 'bold')
          .text(line);
      });
    });

    // Create area shape with closed path
    const areaData = [...validScores, validScores[0]]; // Add first point to close the shape
    const radialLine = d3.lineRadial()
      .angle((d, i) => angleScale(i % validScores.length))
      .radius(d => radiusScale(d))
      .curve(d3.curveLinearClosed);

    // Draw the connecting lines between points
    const points = validScores.map((score, i) => ({
      x: radiusScale(score) * Math.cos(angleScale(i) - Math.PI/2),
      y: radiusScale(score) * Math.sin(angleScale(i) - Math.PI/2)
    }));
    
    // Close the path by adding the first point again
    points.push(points[0]);

    // Create the line generator
    const lineGenerator = d3.line()
      .x(d => d.x)
      .y(d => d.y);

    // Draw the shape with fill and stroke
    svgGroup.append('path')
      .datum(points)
      .attr('d', lineGenerator)
      .attr('fill', COLORS.shapeFill)
      .attr('stroke', COLORS.primaryDark)
      .attr('stroke-width', 2)
      .attr('opacity', 1);

    // Add interaction points
    svgGroup.selectAll('.point')
      .data(validScores)
      .enter()
      .append('circle')
      .attr('class', 'point')
      .attr('cx', (d, i) => radiusScale(d) * Math.cos(angleScale(i) - Math.PI/2))
      .attr('cy', (d, i) => radiusScale(d) * Math.sin(angleScale(i) - Math.PI/2))
      .attr('r', 6)
      .attr('fill', COLORS.primary)
      .attr('stroke', 'white')
      .attr('stroke-width', 2);

    // Add score labels
    svgGroup.selectAll('.score')
      .data(validScores)
      .enter()
      .append('text')
      .attr('class', 'score')
      .attr('x', (d, i) => (radiusScale(d) + 15) * Math.cos(angleScale(i) - Math.PI/2))
      .attr('y', (d, i) => (radiusScale(d) + 15) * Math.sin(angleScale(i) - Math.PI/2))
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', COLORS.primaryDark)
      .attr('font-size', '12px')
      .attr('font-weight', 'bold')
      .text(d => d);

    // Cleanup function
    return () => {
      if (currentSvg) {
        d3.select(currentSvg).selectAll('*').remove();
      }
    };
  }, [svgRef, dimensions, areas, scores]); // Include dimensions object in dependencies

  return (
    <div style={{ 
      position: 'relative',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <svg ref={svgRef} style={{ maxWidth: '100%', height: 'auto' }}></svg>
    </div>
  );
};
function CreateLifeWheel() {
  const [activeStep, setActiveStep] = useState(0);
  const [totalAreas, setTotalAreas] = useState(TOTAL_AREAS);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    areas: Array(TOTAL_AREAS).fill(''),
    scores: Array(TOTAL_AREAS).fill(0),
  });
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [copied, setCopied] = useState(false);
  const [savedWheelId, setSavedWheelId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Handle notification close
  const handleNotificationClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(prev => ({ ...prev, open: false }));
  };

  const steps = [
    {
      label: 'Personal Info',
      description: 'Enter your account details',
    },
    {
      label: 'Life Areas',
      description: 'The AREAS which capture and represent your entire life',
    },
    {
      label: 'Score Areas',
      description: 'Scoring your SATISFACTION LEVEL, with each are of your life.',
    },
    {
      label: 'Your Life Wheel',
      description: 'View and save your assessment',
    },
  ];

  const handleTotalAreasChange = (_, newValue) => {
    if (!newValue) return;
    setTotalAreas(newValue);
    setFormData(prev => ({
      ...prev,
      areas: Array(newValue).fill('').map((_, i) => prev.areas[i] || ''),
      scores: Array(newValue).fill(0).map((_, i) => prev.scores[i] || 0),
    }));
    setErrors({});
  };

  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      // Validate name
      if (!formData.name.trim()) {
        newErrors.name = 'Please enter your name';
      }

      // Validate email
      if (!formData.email.trim()) {
        newErrors.email = 'Please enter your email';
      } else if (!validateEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    } else if (step === 1) {
      // Validate all 10 areas
      const areaSet = new Set();
      formData.areas.forEach((area, index) => {
        if (!area.trim()) {
          newErrors[`area${index}`] = 'Please fill in this area';
        } else if (areaSet.has(area.trim())) {
          newErrors[`area${index}`] = 'Area names must be unique';
        }
        areaSet.add(area.trim());
      });
    } else if (step === 2) {
      // Validate that all areas have been scored
      formData.scores.forEach((score, index) => {
        if (!score) {
          newErrors[`score${index}`] = `Please rate ${formData.areas[index]}`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      
      // Get SVG and convert to base64 image
      const svg = document.querySelector('svg');
      if (!svg) {
        throw new Error('SVG not found');
      }
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      // Set canvas size to match SVG
      canvas.width = svg.width.baseVal.value;
      canvas.height = svg.height.baseVal.value;
      
      // Create a data URL from the SVG string
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      
      // Wait for image to load before converting to PNG
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });
      
      // Draw image and get data URL
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const imageData = canvas.toDataURL('image/png');
      
      // Clean up
      URL.revokeObjectURL(url);

      // Save to database
      const response = await fetch(`${API_BASE_URL}/api/wheels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          areas: formData.areas,
          scores: formData.scores,
          imageData,
          averageScore: calculateAverageScore()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save life wheel');
      }
      
      const data = await response.json();
      setSavedWheelId(data.wheelId);

      // Show success notification
      setNotification({
        open: true,
        message: 'Life Wheel saved! Check your email for the view link',
        severity: 'success'
      });
      setIsSaving(false);
    } catch (error) {
      console.error('Error creating life wheel:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to save Life Wheel. Please try again.',
        severity: 'error'
      });
      setIsSaving(false);
    }
  };

  const calculateAverageScore = () => {
    const validScores = formData.scores.filter((_, i) => formData.areas[i].trim() !== '');
    if (validScores.length === 0) return 0;
    const sum = validScores.reduce((acc, score) => acc + score, 0);
    const average = sum / validScores.length;
    return Math.round(average * 10); // Convert to percentage
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!errors.email}
              helperText={errors.email}
              margin="normal"
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8f9fa' }}>
              <Typography variant="h6" gutterBottom sx={{ textAlign: 'center'}}>
                Life Areas
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
              Spend some time identifying headings (areas) which, collectively, capture your entire life.
              Examples of such areas might include, Job, Finance, Family etc. <br/><br/>  Don't rush this! It is important that you reflect, revise and review until you are satisfied that every aspect of your life falls somewhere under one of the headings you have identified.              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  How many areas do you want to assess?
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {[
                    { value: 8, label: '8 Areas', desc: 'Focused & concise' },
                    { value: 10, label: '10 Areas', desc: 'Comprehensive' },
                  ].map(({ value, label, desc }) => {
                    const selected = totalAreas === value;
                    return (
                      <Box
                        key={value}
                        onClick={() => handleTotalAreasChange(null, value)}
                        sx={{
                          flex: 1,
                          cursor: 'pointer',
                          border: '2px solid',
                          borderColor: selected ? '#1990bb' : '#e0e0e0',
                          borderRadius: 2,
                          p: 2,
                          textAlign: 'center',
                          backgroundColor: selected ? 'rgba(25,144,187,0.08)' : '#fff',
                          transition: 'all 0.15s',
                          '&:hover': { borderColor: '#1990bb', backgroundColor: 'rgba(25,144,187,0.04)' },
                        }}
                      >
                        <Typography variant="h4" sx={{ color: selected ? '#1990bb' : '#aaa', fontWeight: 'bold', lineHeight: 1 }}>
                          {value}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: selected ? '#1990bb' : '#555', mt: 0.5 }}>
                          {label}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {desc}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gap: 2 }}>
                {formData.areas.map((area, index) => (
                  <TextField
                    key={index}
                    fullWidth
                    label={`Life Area ${index + 1}`}
                    placeholder="Enter life area name"
                    value={area}
                    onChange={(e) => {
                      const newAreas = [...formData.areas];
                      newAreas[index] = e.target.value;
                      setFormData({ ...formData, areas: newAreas });
                    }}
                    error={!!errors[`area${index}`]}
                    helperText={errors[`area${index}`]}
                    InputProps={{
                      sx: {
                        bgcolor: 'white',
                      },
                    }}
                  />
                ))}
              </Box>
            </Paper>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8f9fa' }}>
              <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
                Score Your Life Areas
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3, textAlign: 'justify' }}>
                Great work on identifying the areas which capture your life. Step 2 requires that you 'score' how satisfied you are with each area of your life. Be entirely honest with yourself. Use the scale of 1 – 10 (where 1 represents low satisfaction, 5 represents reasonable satisfaction and 10 represents no scope for further satisfaction. Again, take time and be entirely honest with yourself.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {formData.areas.map((area, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      p: 2,
                      borderRadius: 1,
                      bgcolor: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 2, sm: 3 }
                    }}
                  >
                    <Box sx={{ 
                      flex: '1',
                      minWidth: { xs: '100%', sm: '200px' }
                    }}>
                      <Typography 
                        sx={{ 
                          fontWeight: 500,
                          color: errors[`score${index}`] ? 'error.main' : 'inherit',
                          fontSize: '1rem',
                          lineHeight: 1.5
                        }}
                      >
                        {area}
                      </Typography>
                      {errors[`score${index}`] && (
                        <Typography 
                          color="error" 
                          variant="caption"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {errors[`score${index}`]}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ 
                      flex: '1',
                      width: '100%'
                    }}>
                      <ToggleButtonGroup
                        value={formData.scores[index]}
                        exclusive
                        onChange={(e, newValue) => {
                          if (newValue !== null) {
                            const newScores = [...formData.scores];
                            newScores[index] = newValue;
                            setFormData({ ...formData, scores: newScores });
                          }
                        }}
                        size="medium"
                        sx={{ 
                          width: '100%',
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'repeat(5, 1fr)',
                            sm: 'repeat(10, 1fr)'
                          },
                          gap: '4px',
                          '& .MuiToggleButtonGroup-grouped': {
                            borderRadius: '4px !important',
                            border: '1px solid rgba(0, 0, 0, 0.12) !important',
                            m: 0,
                            flex: 1
                          }
                        }}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => {
                          const isSelected = formData.scores[index] === score;
                          return (
                            <ToggleButton 
                              key={score} 
                              value={score}
                              sx={{
                                minWidth: '44px',
                                height: '44px',
                                p: 0,
                                fontSize: '1rem',
                                transition: 'all 0.2s',
                                fontWeight: isSelected ? 600 : 400,
                                '&:hover': {
                                  bgcolor: isSelected ? '#1990bb' : 'rgba(25, 144, 187, 0.08)',
                                  color: isSelected ? '#fff' : '#1990bb',
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#1990bb !important',
                                  color: 'white',
                                  transform: 'scale(1.1)',
                                },
                              }}
                            >
                              {score}
                            </ToggleButton>
                          );
                        })}
                      </ToggleButtonGroup>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
              <Box sx={{ width: '100%', mx: 'auto' }}>
                <Typography variant="h5" gutterBottom sx={{ 
                  mb: { xs: 2, sm: 3 }, 
                  textAlign: 'center',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}>
                  Your WHEEL OF LIFE SCORE
                </Typography>
                <Typography 
                  variant="body1" 
                  gutterBottom 
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    textAlign: { xs: 'left', sm: 'justify' },
                    px: { xs: 1, sm: 0 }
                  }}
                >
                The illustration below captures the 'shape' of your wheel of life.  The shape of your wheel is important. Where the wheel is less circular, the more your journey will be 'uneven' (and less fulfilling).  Most of us have high scoring areas and some areas where satisfaction levels are quite low. Working with your CCO Coach, we now can work through the lower scoring areas, identifying small steps to meaningfully increase your satisfaction level. 
                </Typography>
                <Box 
                  sx={{ 
                    height: { xs: 350, sm: 600, md: 800 },
                    width: '100%',
                    mb: { xs: 2, sm: 3 },
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'visible',
                    mt: { xs: 2, sm: 3 }
                  }}
                >
                  <LifeWheel 
                    areas={formData.areas.filter(area => area.trim() !== '')} 
                    scores={formData.scores.filter((_, i) => formData.areas[i].trim() !== '')}
                    width={800}
                    height={800}
                  />
                </Box>
                <Box sx={{ 
                  borderTop: '2px solid rgba(25, 144, 187, 0.2)', 
                  pt: { xs: 2, sm: 3 }, 
                  mt: { xs: 1, sm: 2 },
                  textAlign: 'center'
                }}>
                  <Typography 
                    variant="h6" 
                    color="primary" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '1.125rem', sm: '1.25rem' }
                    }}
                  >
                    Satisfaction Score: {calculateAverageScore()}%
                  </Typography>
                </Box>
                {savedWheelId && (
                  <Box sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    bgcolor: '#f5f5f5', 
                    borderRadius: 1,
                    mt: { xs: 2, sm: 3 }
                  }}>
                    <Typography 
                      variant="subtitle1" 
                      gutterBottom 
                      color="textSecondary"
                      sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        textAlign: 'center'
                      }}
                    >
                      Your Life Wheel ID
                    </Typography>
                    <Typography 
                      variant="h5" 
                      color="primary" 
                      sx={{ 
                        fontFamily: 'monospace',
                        letterSpacing: { xs: '1px', sm: '2px' },
                        userSelect: 'all',
                        cursor: 'pointer',
                        fontSize: { xs: '1.125rem', sm: '1.5rem' },
                        textAlign: 'center',
                        wordBreak: 'break-all',
                        mt: { xs: 1, sm: 0 }
                      }}
                    >
                      {savedWheelId}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="textSecondary" 
                      sx={{ 
                        mt: { xs: 1, sm: 2 },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        textAlign: 'center'
                      }}
                    >
                      Click to select and copy this ID to access your Life Wheel later
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom align="center">
        Life Wheel Assessment
      </Typography>
      <Box sx={{ mb: 4 }}>
        <Stepper 
          activeStep={activeStep} 
          sx={{ 
            mb: 2,
            '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { 
              color: '#1990bb' 
            },
            '& .MuiStepLabel-label': {
              typography: {
                xs: 'caption',  // Smaller font on mobile
                sm: 'body2'     // Normal font on tablet/desktop
              },
              mt: { xs: 0.5, sm: 1 }
            },
            '& .MuiStepper-root': {
              flexDirection: { xs: 'column', sm: 'row' }
            },
            '& .MuiStep-root': {
              py: { xs: 0.5, sm: 1 }
            },
            '& .MuiStepIcon-root': {
              width: { xs: '1.5rem', sm: '2rem' },
              height: { xs: '1.5rem', sm: '2rem' }
            }
          }}
        >
          {steps.map((step) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Typography 
          variant="h5" 
          color="textSecondary" 
          align="center"
          sx={{
            typography: { xs: 'body1', sm: 'h5' },
            px: { xs: 2, sm: 0 },
            mt: { xs: 1, sm: 0 }
          }}
        >
          {steps[activeStep].description}
        </Typography>
      </Box>
      <Box>
        {renderStepContent(activeStep)}
        <Paper elevation={0} sx={{ mt: 4, p: 2, bgcolor: '#f8f9fa' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              {activeStep > 0 && (
                <Button
                  onClick={handleBack}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.05)',
                    },
                  }}
                  startIcon={<span>←</span>}
                  disabled={savedWheelId !== null}
                >
                  Back
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>
                Step {activeStep + 1} of {steps.length}
              </Typography>
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<span>→</span>}
                  sx={{
                    minWidth: 100,
                    bgcolor: '#1990bb',
                    '&:hover': {
                      bgcolor: '#157a9c',
                    },
                  }}
                >
                  Next
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {savedWheelId ? (
                    <Button
                      variant="contained"
                      onClick={() => {
                        navigator.clipboard.writeText(savedWheelId);
                        setCopied(true);
                        setNotification({
                          open: true,
                          message: 'Wheel ID copied to clipboard!',
                          severity: 'success'
                        });
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      sx={{
                        minWidth: 140,
                        bgcolor: copied ? '#4caf50' : '#1990bb',
                        '&:hover': {
                          bgcolor: copied ? '#45a049' : '#157a9c',
                        },
                      }}
                      endIcon={copied ? <span>✓</span> : <span>📋</span>}
                    >
                      {copied ? 'Copied!' : 'Copy Wheel ID'}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      endIcon={<span>✓</span>}
                      disabled={isSaving}
                      sx={{
                        minWidth: 140,
                        bgcolor: '#1990bb',
                        '&:hover': {
                          bgcolor: '#157a9c',
                        },
                      }}
                    >
                      {isSaving ? 'Saving...' : 'Save Life Wheel'}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default CreateLifeWheel;
